import { useState } from 'react';
import {
  FRAMEWORKS_BY_CATEGORY,
  CATEGORY_META,
  type FrameworkId,
  type FrameworkCategory,
} from '@seedgovernance/shared';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';

interface FrameworkSelectProps {
  maxFrameworks: number;
  preselected: FrameworkId[];
  onNext: (frameworkIds: FrameworkId[]) => void;
  onBack: () => void;
}

export function FrameworkSelect({
  maxFrameworks,
  preselected,
  onNext,
  onBack,
}: FrameworkSelectProps) {
  const [selected, setSelected] = useState<Set<FrameworkId>>(
    new Set(preselected)
  );

  function toggle(id: FrameworkId) {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else if (maxFrameworks === 0 || next.size < maxFrameworks) {
      next.add(id);
    }
    setSelected(next);
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-display font-bold text-white mb-2">
          Select Your Frameworks
        </h1>
        <p className="text-slate-400">
          Choose the compliance frameworks relevant to your organization.
          {maxFrameworks > 0 && (
            <span className="text-brand-light">
              {' '}You can select up to {maxFrameworks}.
            </span>
          )}
        </p>
        <Badge variant="brand" className="mt-2">
          {selected.size}{maxFrameworks > 0 ? ` / ${maxFrameworks}` : ''} selected
        </Badge>
      </div>

      <div className="space-y-6">
        {(Object.entries(FRAMEWORKS_BY_CATEGORY) as [FrameworkCategory, typeof FRAMEWORKS_BY_CATEGORY[FrameworkCategory]][]).map(
          ([category, frameworks]) => (
            <div key={category}>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {CATEGORY_META[category]?.label || category}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {frameworks.map((fw) => {
                  const isSelected = selected.has(fw.id);
                  const isDisabled =
                    !isSelected && maxFrameworks > 0 && selected.size >= maxFrameworks;

                  return (
                    <button
                      key={fw.id}
                      onClick={() => toggle(fw.id)}
                      disabled={isDisabled}
                      className={`text-start p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-brand bg-brand/5'
                          : isDisabled
                          ? 'border-border bg-surface-dark opacity-50 cursor-not-allowed'
                          : 'border-border bg-surface-mid hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: fw.color }}
                        />
                        <span className="text-sm text-white font-medium">
                          {fw.shortName}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ps-5">
                        {fw.docCount} templates
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>

      <div className="flex gap-3 mt-8">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          Back
        </Button>
        <Button
          className="flex-1"
          disabled={selected.size === 0}
          onClick={() => onNext(Array.from(selected))}
        >
          Continue ({selected.size} selected)
        </Button>
      </div>
    </div>
  );
}
